import debounce from "lodash.debounce";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";
import { useDebounce } from "use-debounce";
import { v4 as createUuid } from "uuid";

import type { TopicResult } from "api/api.types";
import { dimensionsApiSlice } from "api/dimensionApi.slice";
import { topicsApiSlice, useLazyGetSimilarTopicsByDimensionQuery } from "api/topicsApi.slice";
import { AddItemIcon, CrossCircledIcon, ErrorMessage, SectionTitleUnderlined, UIButton } from "common/components";
import { Autocomplete } from "common/components/form/Autocomplete/Autocomplete";
import { ClearInput } from "common/components/form/ClearInput";
import { TextArea } from "common/components/form/TextArea";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { addNewCustomKeyIssueToMap, setNewCustomDimensionTopics } from "domains/createMapDetail/actions";
import { getCurrentMap, getCurrentMapIsNew } from "domains/createMapDetail/selectors";
import { createCustomKeyIssue } from "domains/customKeyIssue/actions";
import { type SelectedTopicOption, SelectedTopics } from "domains/customKeyIssue/components/SelectedTopics";
import { StyledSubtitle } from "domains/customKeyIssue/components/shared";
import { SimilarKnowledge } from "domains/customKeyIssue/components/SimilarKnowledge";
import { SuggestedTopics } from "domains/customKeyIssue/components/SuggestedTopics";
import { getCustomKeyIssueIsUploading, getCustomKeyIssueUploadHasErrored } from "domains/customKeyIssue/selectors";
import { useUploadDimension } from "domains/customKeyIssue/useUploadDimension";
import { dimensionSlice } from "domains/dimension/dimension.slice";
import type { NewCustomDimension } from "domains/dimension/dimension.types";

type DimensionTextData = { description: string; name: string; parent_topic_name: string; subtitle: string };

export const AddCustomKeyIssue = () => {
  const dispatch = useDispatch();
  const [fetchSimilarTopicsByDimension, similarTopicsResult] = useLazyGetSimilarTopicsByDimensionQuery();
  const currentMap = useSelector(getCurrentMap);
  const isNewCustomMap = useSelector(getCurrentMapIsNew);
  const isUploading = useSelector(getCustomKeyIssueIsUploading);
  const hasUploadFailed = useSelector(getCustomKeyIssueUploadHasErrored);
  const [selectedTopics, setSelectedTopics] = useState<SelectedTopicOption[]>([]);
  const translate = useTranslate();
  const { dimension, isFetching } = useUploadDimension();
  const [filterTerm, setFilterTerm] = useState("");
  const [debouncedFilterTerm] = useDebounce(filterTerm, 500);

  const [description, setDescription] = useState(dimension?.description ?? "");
  const [subtitle, setSubtitle] = useState(dimension?.subtitle ?? "");
  const [title, setTitle] = useState(dimension?.name ?? "");

  const {
    data: paginatedTopicSearchResults,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage: fetchNextResultsPage,
  } = topicsApiSlice.endpoints.searchTopics.useInfiniteQuery({
    search: debouncedFilterTerm,
  });

  const loadNextPage = () => {
    if (!hasNextPage || isFetchingNextPage) return;

    return fetchNextResultsPage();
  };

  const topicSearchResults = paginatedTopicSearchResults?.pages.flat() ?? [];

  const isCreateMode = dimension === undefined;

  useEffect(() => {
    if (!isCreateMode) setSelectedTopics(dimension.topics);
  }, [dimension?.topics, isCreateMode]);

  useEffect(() => {
    if (isCreateMode) return;
    void fetchSimilarTopics({ description, name: title, parent_topic_name: currentMap?.name ?? "", subtitle });
    setSelectedTopics(dimension.topics);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode]);

  const fetchSimilarTopics = (data: DimensionTextData) =>
    fetchSimilarTopicsByDimension(
      { body: { data, ignore_ids: currentMap?.id === undefined ? null : [currentMap.id] }, search: { limit: 5 } },
      true,
    );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSimilarTopics = useCallback(
    debounce(fetchSimilarTopics, 500, { leading: true, trailing: true }),
    [],
  );

  const onChangeTitle = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setTitle(value);
    void debouncedFetchSimilarTopics({ description, name: value, parent_topic_name: currentMap?.name ?? "", subtitle });
  };

  const onChangeSubtitle = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setSubtitle(value);
    void debouncedFetchSimilarTopics({
      description,
      name: title,
      parent_topic_name: currentMap?.name ?? "",
      subtitle: value,
    });
  };

  const onChangeDescription = (value: string) => {
    setDescription(value);
    void debouncedFetchSimilarTopics({
      description: value,
      name: title,
      parent_topic_name: currentMap?.name ?? "",
      subtitle,
    });
  };

  const onAddTopicSuggestion = (item: { id: string; name: string }) => {
    const topic = similarTopicsResult.data?.find(({ id }) => id === item.id);
    if (topic === undefined) return;
    const topics = [...selectedTopics.filter(({ id }) => id !== item.id), topic];
    setSelectedTopics(topics);
    dispatch(setNewCustomDimensionTopics({ id: item.id, topics }));
  };

  const onClearTitle = () => {
    setTitle("");
    void fetchSimilarTopics({ description, name: "", parent_topic_name: currentMap?.name ?? "", subtitle });
  };

  const onClearSubtitle = () => {
    setSubtitle("");
    void fetchSimilarTopics({ description, name: title, parent_topic_name: currentMap?.name ?? "", subtitle: "" });
  };

  const onMapSelected = (item: SelectedTopicOption) => {
    const topic = topicSearchResults.find(({ id }) => id === item.id);
    if (topic !== undefined) setSelectedTopics([...selectedTopics, topic]);
  };

  const onTopicsRemoved = (removedItem: SelectedTopicOption) =>
    setSelectedTopics(selectedTopics.filter(({ id }) => id !== removedItem.id));

  const onSelectOptionPress = (option: SelectedTopicOption) => {
    if (!selectedTopics.find((selectedTopic) => selectedTopic.id === option.id)) {
      onMapSelected(option);
    } else {
      onTopicsRemoved(option);
    }
  };

  const onSubmit = () => {
    const body: NewCustomDimension = {
      description,
      id: dimension?.id ?? createUuid(),
      image_url: dimension?.image_url ?? null,
      is_custom_dimension: true,
      name: title,
      parent_topic_id: currentMap?.id,
      status: "Active",
      subtitle,
      topics: selectedTopics,
    };

    if (isNewCustomMap) {
      dispatch(addNewCustomKeyIssueToMap(body));
      return;
    }

    const dimensionCacheUpdateAction = dimensionsApiSlice.util.updateQueryData(
      "getDimension",
      { id: body.id },
      (draft) => {
        draft.dimension.description = body.description;
        draft.dimension.name = body.name;
        draft.dimension.subtitle = body.subtitle;
        draft.dimension.topic_ids = body.topics.map((topic) => topic.id);
        draft.topics = body.topics as TopicResult[];
      },
    );
    dispatch(dimensionCacheUpdateAction);
    dispatch(dimensionSlice.actions.sendRefetchSignal());
    dispatch(createCustomKeyIssue(body));
  };

  const validateTitleCharLength = title.length <= TITLE_CHAR_LIMIT;
  const isValid = !!title && validateTitleCharLength && selectedTopics.length !== 0;
  const isFieldsDisabled = isFetching || isUploading;

  const term = isNewCustomMap
    ? isCreateMode
      ? "app.create.create_map.add"
      : "app.create.create_map.update"
    : isCreateMode
      ? "app.create.create_map.add"
      : "app.create.create_map.save";

  const suggestedTopics = similarTopicsResult.isFetching
    ? []
    : (similarTopicsResult.data
        ?.filter(({ id }) => !selectedTopics.some((topic) => topic.id === id))
        .map(({ id, name }) => ({ name, id })) ?? []);

  return (
    <>
      <StyledCharLimitLabel valid={validateTitleCharLength}>
        {title.length}/{TITLE_CHAR_LIMIT}
      </StyledCharLimitLabel>
      <StyledInput
        autoComplete="off"
        dataTestId="add-custom-key-issue-title-input"
        disabled={isFieldsDisabled}
        label={translate("app.custom_key_issue.title")}
        name="key-issue-title"
        onChange={onChangeTitle}
        onClearHandle={onClearTitle}
        value={title}
      />
      <StyledInput
        dataTestId="add-custom-key-issue-subtitle-input"
        disabled={isFieldsDisabled}
        label={translate("app.custom_key_issue.subtitle")}
        name="subtitle"
        onChange={onChangeSubtitle}
        onClearHandle={onClearSubtitle}
        value={subtitle}
      />
      <TextArea
        dataTestId="add-custom-key-issue-description-textarea"
        disabled={isFieldsDisabled}
        name="add-custom-key-issue-description-textarea"
        onValueChange={onChangeDescription}
        placeholder={translate("app.custom_key_issue.description")}
        value={description}
      />
      <StyledSectionTitleUnderlined>{translate("app.custom_key_issue.connect_to_maps")}</StyledSectionTitleUnderlined>
      <StyledSubtitle>{translate("app.custom_key_issue.search_topics.help_text")}</StyledSubtitle>
      <SuggestedTopics
        suggestedTopics={suggestedTopics}
        isDisabled={isFieldsDisabled}
        onSuggestionPress={onAddTopicSuggestion}
      />
      <SelectedTopics
        selectedTopics={selectedTopics}
        isDisabled={isFieldsDisabled}
        onSelectedTopicPress={onTopicsRemoved}
      />
      <Autocomplete
        id="topics-dropdown-select-input"
        value={filterTerm}
        placeholder={translate("app.collection.search_for_topics")}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        options={topicSearchResults.map((topic) => ({
          icon: selectedTopics.some((selectedTopic) => selectedTopic.id === topic.id) ? (
            <CrossCircledIcon />
          ) : (
            <AddItemIcon />
          ),
          iconLabel: selectedTopics.some((selectedTopic) => selectedTopic.id === topic.id)
            ? translate("app.create.collection.topics.remove")
            : translate("app.create.collection.topics.add"),
          image: topic.image_url,
          id: topic.id,
          name: topic.name,
          onClick: () => onSelectOptionPress(topic),
        }))}
        onChange={setFilterTerm}
        onClear={() => setFilterTerm("")}
        onEndReached={loadNextPage}
      />
      <SimilarKnowledge description={description} name={title} subtitle={subtitle} />
      <StyledErrorMessage isActive={hasUploadFailed}>{translate("app.auth.error")}</StyledErrorMessage>
      <UIButton
        data-test-id="submit-new-custom-key-issue-button"
        disabled={!isValid || isFetching}
        fullwidth
        isLoading={isUploading}
        onClick={onSubmit}
        style={{ margin: "1rem 0" }}
      >
        {translate(term)}
      </UIButton>
    </>
  );
};

const TITLE_CHAR_LIMIT = 60;

const StyledCharLimitLabel = styled.p<{ valid: boolean }>`
  display: flex;
  justify-content: flex-end;
  font-size: 0.8rem;
  color: ${({ theme, valid }) => (valid ? theme.colors.textFade : theme.colors.red)};
`;

const StyledInput = styled(ClearInput)`
  margin-bottom: 1rem;
`;

const StyledSectionTitleUnderlined = styled(SectionTitleUnderlined)`
  margin-top: 2rem;
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 0;
`;
